import React, {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET, POST} from '../../../services/api'
import Swal from 'sweetalert2'
import {useNavigate} from 'react-router-dom'
import SVG from 'react-inlinesvg'
import useModals from '../../../hooks/useModals'
import {IconPickerModal} from '../ContentStructured/ServiceResquests/components/IconPickerModal'
import {EntertainmentPickerModal, EntertainmentItem} from './components/EntertainmentPickerModal'

type SvgIcon = {
  id: number
  description?: string | null
  svg?: string | null
  class?: string | null
}

type ReviewHotelCategoryItem = {
  id: number
  title: string
}

const ReviewHotelCreateForm: FC = () => {
  const navigate = useNavigate()
  const {showWrapperModal} = useModals()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Opções vindas do backend
  const [icons, setIcons] = useState<SvgIcon[]>([])
  const [categories, setCategories] = useState<ReviewHotelCategoryItem[]>([])
  const [entertainments, setEntertainments] = useState<EntertainmentItem[]>([])

  // Campos do formulário
  const [title, setTitle] = useState('')
  const [order, setOrder] = useState('0')
  const [status, setStatus] = useState('true')
  const [activeRating, setActiveRating] = useState('true')
  const [activeDetails, setActiveDetails] = useState('true')
  const [categoryId, setCategoryId] = useState<string>('')

  // Estado do Entretenimento Selecionado
  const [selectedEntertainment, setSelectedEntertainment] = useState<EntertainmentItem | null>(null)
  const [isEntertainmentModalOpen, setIsEntertainmentModalOpen] = useState(false)

  // Estados do Ícone
  const [iconId, setIconId] = useState<number | null>(null)
  const [iconSvg, setIconSvg] = useState<string>('')
  const [iconClass, setIconClass] = useState<string>('')

  // Estados para o Modal de Ícones
  const [selectedIcon, setSelectedIcon] = useState<string>('')
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [iconsRes, catRes, expRes, actRes, tourRes] = await Promise.all([
        POST('/svgwebapp/filter', {}),
        GET('/review-hotel-category'),
        GET('/hotel-experiences'),
        GET('/hotel-activities'),
        GET('/hotel-tours'),
      ])

      const iconsList = Array.isArray(iconsRes?.data)
        ? iconsRes.data
        : Array.isArray(iconsRes?.dados)
        ? iconsRes.dados
        : []
      const catList = Array.isArray(catRes?.data)
        ? catRes.data
        : Array.isArray(catRes?.dados)
        ? catRes.dados
        : []

      const expList = Array.isArray(expRes?.data)
        ? expRes.data
        : Array.isArray(expRes?.dados)
        ? expRes.dados
        : []
      const actList = Array.isArray(actRes?.data)
        ? actRes.data
        : Array.isArray(actRes?.dados)
        ? actRes.dados
        : []
      const tourList = Array.isArray(tourRes?.data)
        ? tourRes.data
        : Array.isArray(tourRes?.dados)
        ? tourRes.dados
        : []

      // Unificando as listas para o modal
      const combinedEntertainments: EntertainmentItem[] = [
        ...expList.map((item: any) => ({
          id: item.id,
          title: item.title,
          type: 'experience' as const,
          typeLabel: 'Experiência',
        })),
        ...actList.map((item: any) => ({
          id: item.id,
          title: item.title,
          type: 'activity' as const,
          typeLabel: 'Atividade',
        })),
        ...tourList.map((item: any) => ({
          id: item.id,
          title: item.title,
          type: 'tour' as const,
          typeLabel: 'Passeio / Tour',
        })),
      ]

      setIcons(iconsList)
      setCategories(catList)
      setEntertainments(combinedEntertainments)
    } catch (error) {
      console.log(error)
      setIcons([])
      setCategories([])
      setEntertainments([])
    } finally {
      setLoading(false)
    }
  }

  const openIconPicker = () => {
    setSelectedIcon(iconClass)
    setSelectedIconId(iconId)
    showWrapperModal('Escolher Ícone')
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setLoading(true)
      if (!title.trim()) throw new Error('Informe o título do formulário.')
      if (iconId === null) throw new Error('Selecione um ícone para o formulário.')
      if (activeRating === 'false' && activeDetails === 'false') {
        throw new Error('Ative pelo menos uma opção: Avaliação em Estrelas ou Detalhes Adicionais.')
      }

      const payload = {
        title: title.trim(),
        activeDetails: activeDetails === 'true',
        iconId: iconId,
        order: Number(order) || 0,
        status: status === 'true',
        activeRating: activeRating === 'true',
        reviewHotelCategoryId: categoryId ? Number(categoryId) : null,
        // Envia o ID apenas para a tabela do tipo selecionado
        hotelExperienceId:
          selectedEntertainment?.type === 'experience' ? selectedEntertainment.id : null,
        hotelActivityId:
          selectedEntertainment?.type === 'activity' ? selectedEntertainment.id : null,
        hotelTourId: selectedEntertainment?.type === 'tour' ? selectedEntertainment.id : null,
      }

      const res = await POST('/review-hotel-form', payload)

      if (res?.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Criado com sucesso',
          text: 'O formulário foi salvo e vinculado.',
        })
        navigate('/hospitality/feed-back/content')
      } else {
        throw new Error(res?.message || 'Não foi possível criar o formulário.')
      }
    } catch (error: any) {
      console.log(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao criar',
        text: error?.message || 'Não foi possível criar o formulário.',
      })
    } finally {
      setSaving(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Criar Formulário</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Preencha os campos principais do formulário de opinião.
                </span>
              </h3>
            </div>

            <div className='card-body'>
              <div className='row g-5 align-items-end'>
                <div className='col-md-6'>
                  <Input
                    label='Título'
                    inputAttr={{type: 'default'}}
                    value={title}
                    change={(value: string) => setTitle(value)}
                    placeholder='Ex: Avaliação do Restaurante'
                    maxCaracteres={30}
                    required
                  />
                </div>

                <div className='col-md-6'>
                  <div className='fw-bolder text-dark mb-2'>Ícone</div>
                  <div className='d-flex align-items-center gap-4'>
                    <div
                      className='d-flex align-items-center justify-content-center border border-gray-300 rounded bg-light'
                      style={{width: '44px', height: '44px', flexShrink: 0}}
                    >
                      {iconSvg ? (
                        <SVG src={iconSvg} style={{width: 22, height: 22}} />
                      ) : (
                        <i className={`bi ${iconClass || 'bi-image'} fs-3 text-muted`} />
                      )}
                    </div>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary fw-bolder'
                      onClick={openIconPicker}
                    >
                      Escolher Ícone
                    </button>
                  </div>
                </div>

                <div className='col-md-6'>
                  <Input
                    label='Categoria da Avaliação'
                    inputAttr={{type: 'select'}}
                    value={categoryId}
                    change={(value: string) => setCategoryId(value)}
                    options={[
                      {value: '', select: 'Geral'},
                      ...categories.map((c) => ({value: String(c.id), select: c.title})),
                    ]}
                    marginTop='0'
                  />
                </div>

                {/* NOVO INPUT VISUAL PADRÃO METRONIC */}
                <div className='col-md-6'>
                  <label className='form-label fs-6 fw-bolder text-dark mb-2'>
                    Entretenimento Vinculado (Opcional)
                  </label>
                  <div className='input-group input-group-solid'>
                    <span className='input-group-text bg-light'>
                      <i className='fas fa-link text-muted'></i>
                    </span>
                    <input
                      type='text'
                      className='form-control form-control-solid'
                      readOnly
                      value={
                        selectedEntertainment
                          ? `${selectedEntertainment.title} (${selectedEntertainment.typeLabel})`
                          : ''
                      }
                      placeholder='Nenhum entretenimento vinculado'
                      onClick={() => setIsEntertainmentModalOpen(true)}
                      style={{cursor: 'pointer'}}
                    />
                    {selectedEntertainment && (
                      <button
                        type='button'
                        className='btn btn-icon btn-light-danger'
                        title='Remover vínculo'
                        onClick={() => setSelectedEntertainment(null)}
                      >
                        <i className='fas fa-times'></i>
                      </button>
                    )}
                    <button
                      type='button'
                      className='btn btn-light-primary fw-bolder'
                      onClick={() => setIsEntertainmentModalOpen(true)}
                    >
                      {selectedEntertainment ? 'Trocar' : 'Selecionar'}
                    </button>
                  </div>
                </div>

                <div className='col-md-4'>
                  <Input
                    label='Ordem'
                    inputAttr={{type: 'number'}}
                    value={order}
                    change={(value: string) => setOrder(value)}
                    placeholder='0'
                    marginTop='0'
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    label='Status'
                    inputAttr={{type: 'select'}}
                    value={status}
                    change={(value: string) => setStatus(value)}
                    options={[
                      {value: 'true', select: 'Ativo'},
                      {value: 'false', select: 'Inativo'},
                    ]}
                    marginTop='0'
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    label='Avaliação em Estrelas'
                    inputAttr={{type: 'select'}}
                    value={activeRating}
                    change={(value: string) => setActiveRating(value)}
                    options={[
                      {value: 'true', select: 'Ativada'},
                      {value: 'false', select: 'Desativada'},
                    ]}
                    marginTop='0'
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    label='Detalhes Adicionais'
                    inputAttr={{type: 'select'}}
                    value={activeDetails}
                    change={(value: string) => setActiveDetails(value)}
                    options={[
                      {value: 'true', select: 'Ativado'},
                      {value: 'false', select: 'Desativado'},
                    ]}
                    marginTop='0'
                  />
                </div>
              </div>
            </div>

            <div className='card-footer d-flex justify-content-end gap-3 py-6'>
              <Button
                text='Voltar'
                color='info'
                size='small'
                click={() => navigate('/hospitality/feed-back/content')}
                btnClass='fw-bolder hover-scale'
              />
              <Button
                text='Salvar'
                color='primary'
                size='small'
                click={handleSave}
                loading={saving}
                btnClass='fw-bolder hover-scale'
              />
            </div>
          </div>
        </div>
      )}

      <IconPickerModal
        isOpen={false}
        wrapperModalOpen={true}
        icons={icons}
        selectedIconId={selectedIconId}
        selectedIcon={selectedIcon}
        setSelectedIcon={setSelectedIcon}
        setSelectedIconId={setSelectedIconId}
        onClose={() => {
          setSelectedIcon('')
          setSelectedIconId(null)
        }}
        onConfirm={() => {
          if (selectedIconId != null) {
            const chosen = icons.find((i: any) => i.id === selectedIconId)
            if (chosen?.svg) setIconSvg(chosen.svg)
            setIconId(selectedIconId)
          } else {
            setIconSvg('')
            setIconId(null)
          }
          setIconClass(selectedIcon || '')
        }}
      />

      <EntertainmentPickerModal
        isOpen={isEntertainmentModalOpen}
        onClose={() => setIsEntertainmentModalOpen(false)}
        items={entertainments}
        onSelect={(item) => {
          setSelectedEntertainment(item)
          setIsEntertainmentModalOpen(false)
        }}
      />
    </>
  )
}

const ReviewHotelCreate: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: 'Formulários de Opinião', path: '/hospitality/feed-back/content', isActive: true},
          {title: 'Criar', path: '#', isActive: true},
        ]}
      >
        Criar Formulário
      </PageTitle>
      <ReviewHotelCreateForm />
    </>
  )
}
export {ReviewHotelCreate}
